import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";
import { isSupabaseConnected, fetchJournalEntries } from "./supabase";
import type { JournalEntry } from "./admin/AdminDataContext";

/** Map DB row to JournalEntry shape for display */
function mapRowToEntry(row: {
  id: number | string;
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  image?: string | null;
  author?: string | null;
  published?: boolean;
  i18n?: Record<string, unknown> | null;
  created_at?: string | null;
}): JournalEntry {
  return {
    id: Number(row.id),
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt ?? "",
    content: row.content ?? "",
    image: row.image ?? "",
    author: row.author ?? "",
    published: row.published ?? true,
    i18n: (row.i18n ?? {}) as JournalEntry["i18n"],
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

export function JournalPage() {
  const { journal: contextJournal } = useAdminData();
  const { lang, t } = useLanguage();
  const theme = useTheme();
  const [published, setPublished] = useState<JournalEntry[]>(() =>
    isSupabaseConnected() ? [] : contextJournal.filter((j) => j.published)
  );
  const [loading, setLoading] = useState(isSupabaseConnected());

  // When Supabase is connected, fetch published entries from DB on mount so the page always shows current data
  useEffect(() => {
    if (!isSupabaseConnected()) {
      setPublished(contextJournal.filter((j) => j.published));
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchJournalEntries()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch journal:", error.message);
          setPublished(contextJournal.filter((j) => j.published));
          return;
        }
        setPublished((data ?? []).map(mapRowToEntry));
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch from DB once on mount when Supabase connected; context fallback when not

  // When Supabase is not connected, keep in sync with context (e.g. localStorage)
  useEffect(() => {
    if (isSupabaseConnected()) return;
    setPublished(contextJournal.filter((j) => j.published));
  }, [contextJournal]);

  return (
    <div className="pt-28 pb-24">
      {/* Header */}
      <div className="text-center mb-16 px-6">
        <p
          className="tracking-[0.3em] uppercase mb-3"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
        >
          {t("journal.label")}
        </p>
        <h1
          className="tracking-[0.1em]"
          style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}
        >
          {t("journal.title")}
        </h1>
      </div>

      {/* Entries */}
      <div className="max-w-4xl mx-auto px-6">
        {loading ? (
          <div className="text-center py-16">
            <p
              style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
            >
              Loading…
            </p>
          </div>
        ) : published.length === 0 ? (
          <div className="text-center py-16">
            <p
              style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
            >
              {t("journal.empty")}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {published.map((entry) => (
              <article
                key={entry.id}
                className="group grid grid-cols-1 md:grid-cols-2 gap-8 items-center"
              >
                <div className="overflow-hidden rounded-lg" style={{ backgroundColor: `${theme.border}40` }}>
                  {entry.image && (
                    <ImageWithFallback
                      src={entry.image}
                      alt={entry.title}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  )}
                </div>
                <div>
                  <p
                    className="tracking-[0.2em] uppercase mb-2"
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.accent }}
                  >
                    {entry.author} &middot; {new Date(entry.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                  <h2
                    className="tracking-[0.06em] mb-4"
                    style={{ fontFamily: theme.fontHeading, fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 400, color: theme.fg }}
                  >
                    {localized(entry, "title", lang)}
                  </h2>
                  <p
                    className="leading-[1.8] mb-6"
                    style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
                  >
                    {localized(entry, "excerpt", lang)}
                  </p>
                  <Link
                    to={`/journal/${entry.slug}`}
                    className="tracking-[0.15em] uppercase border-b pb-0.5 transition-colors"
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary, borderColor: `${theme.primary}4d` }}
                  >
                    {t("journal.read_more")}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
